import { useQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'

import { RecordFactory } from '../../models/RecordFactory'
import { Taxcode } from '../../models/Taxcode'
import { Spinner } from '../Spinner'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import Tag from '../app/design/Tag'
import { AddressPicker } from '../common/AddressPicker'
import { Checkbox } from '../common/fields/Checkbox'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    isOpen: boolean
    currentData?: { location?: string; alwaysCollectTax?: boolean }
    onDone: (location: string, taxcode: Taxcode | undefined, alwaysCollectTax: boolean) => void
    onCancel: () => void
}

export const SetupSalesTaxModal = ({ isOpen, currentData, onDone, onCancel }: Props) => {
    const [location, setLocation] = useState(currentData?.location ?? '')
    const [alwaysCollectTax, setAlwaysCollectTax] = useState(currentData?.alwaysCollectTax ?? false)

    const { data: taxcodeData, isLoading: isLoadingTaxcode } = useQuery({
        queryKey: ['tax-codes', location],
        queryFn: async () => {
            const res = await RecordFactory.getInstance().queryTaxcode(location)
            if (!res.ok) return null
            return res.value
        },
    })

    const doneEnabled = !isLoadingTaxcode && taxcodeData !== null

    const onDoneHandler = useCallback(() => {
        if (!doneEnabled) return
        onDone(location, taxcodeData, alwaysCollectTax)
    }, [doneEnabled, location, taxcodeData, alwaysCollectTax, onDone])

    return (
        <Modal
            isOpen={isOpen}
            onClose={onCancel}
            title="Sales tax"
            footer={
                <Button intent="primary" onClick={onDoneHandler} disabled={!doneEnabled}>
                    Done
                </Button>
            }
        >
            <div className="flex flex-col gap-8">
                <WorkflowPage.IconSection icon="markerPin.large" title="Your business location">
                    <AddressPicker size="medium" defaultAddress={location} onSelect={setLocation} />
                    {taxcodeData === undefined ? (
                        <Spinner size={24} />
                    ) : (
                        <div className="flex items-center justify-center gap-x-1 rounded-lg border border-gray-300 p-3">
                            <span className="font-bold">{taxcodeData?.rate ?? 0}%</span>
                            {taxcodeData?.state && (
                                <span className="text-gray-600">({taxcodeData.state})</span>
                            )}
                        </div>
                    )}
                </WorkflowPage.IconSection>
                <WorkflowPage.IconSection
                    icon="percentage2.large"
                    title="Always collect sales tax"
                    description="Collect sales tax on every transaction"
                    hasHoverEffect
                    secondaryView={
                        <Checkbox isChecked={alwaysCollectTax} onChange={setAlwaysCollectTax} />
                    }
                />
            </div>
        </Modal>
    )
}
